import { ServerEvent } from "@/common/notification";
import {
	useCurrentInteractionStore,
	CurrentInteractionState,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { notificationListener } from "@/hooks/notification";
import { InteractionDto } from "@/repository/models";
import { resumeInteraction } from "@/repository/queryFns";
import { useQueryClient } from "@tanstack/react-query";
import { NewMessageArrivedInConversationNotification } from "@/common/models/emailNotification";
import { captureError } from "@/common/helpers/sentryHelper";
import { useMessage } from "@/common/messages";
import { useIntl } from "react-intl";

export const useMessagingNotifications = (
	chatQueryKey: any[]
) => {
	const queryClient = useQueryClient();

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	notificationListener<InteractionDto>(
		ServerEvent.ActiveMessagingChanged,
		(_: InteractionDto) => {
			queryClient.invalidateQueries({ queryKey: chatQueryKey });
		}
	);

	notificationListener<NewMessageArrivedInConversationNotification>(
		ServerEvent.NewMessageArrivedInConversation,
		(newMessagesNotification: NewMessageArrivedInConversationNotification) => {
			if (
				currentInteraction?.id === newMessagesNotification.sessionId &&
				currentInteraction?.isOnHold
			) {
				resumeInteraction(currentInteraction.id);
			}
		}
	);

	notificationListener<InteractionDto>(
		ServerEvent.SupervisedActiveInteractionChanged,
		(_: InteractionDto) => {
			queryClient.invalidateQueries({ queryKey: chatQueryKey });
		}
	);
};

export const useAddMessagesInChat = (chatQueryKey: any[]) => {
	const queryClient = useQueryClient();

	return (newMessages: any) => {
		queryClient.setQueryData(chatQueryKey, (oldData: any) => {
			if (!oldData) {
				return oldData;
			}

			const updatedPages = [...oldData.pages];
			updatedPages[0] = {
				...updatedPages[0],
				items: [...newMessages, ...updatedPages[0].items],
			};

			return { ...oldData, pages: updatedPages };
		});
	};
};

export const useHandleError = (
	interactionId: string,
	interactionTypeName: string,
	chatQueryKey: any[]
) => {
	const intl = useIntl();
	const queryClient = useQueryClient();
	const { show: showMessage } = useMessage();

	return (ex: Error, context: string) => {
		queryClient.invalidateQueries({ queryKey: chatQueryKey });

		captureError(ex, context, {
			interactionId,
			interactionType: interactionTypeName,
		});
		console.error(context, ex);
		showMessage("workspace", {
			text: intl.formatMessage(
				{
					id: "Interaction.TextEditor.ErrorMessageSent",
				},
				{ chatType: interactionTypeName }
			),
			intent: "error",
		});
	};
};
